import { getRequestConfig } from '../auth/auth';
import { ApiGateway } from '../config/config';
import axios from 'axios';
import bus from '../utils/EventBus'
import Event from '../utils/EventNames'
import { ASMT_STATUS } from '../utils/constants';

export const createManufacturerProfile = async (manufacturer = undefined) => {
  const basePath = `${ApiGateway.manufacturerProfile}`
  const requestConfig = await getRequestConfig()
  try {
    if ( !manufacturer ) throw new Error('No Manufacturer provided')
    const response = await axios.post(basePath, manufacturer, requestConfig)
    
    return response.data
  } catch (error) {
    return {
      status: 'error',
      error
    }
  }
}
export const loadManufacturerProfile = async (search = {id: undefined, manufacturerId: undefined}, requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }
    
  const basePath = `${ApiGateway.manufacturerProfile}/search`
  if ( !search.id && !search.manufacturerId ) return undefined
  
  const response = await axios.post(basePath, search, requestConfig)
  if (response.data) {
    const { results } = response.data
    return results[0]
  }
  return undefined
}

export const saveManufacturerProfile = async (_profile) => {
  const requestConfig = await getRequestConfig()
  
  const basePath = `${ApiGateway.manufacturerProfile}/${_profile.id}`

  const payload = _profile

  try {
    const result = await axios.post(basePath, payload, requestConfig)
    return result
  } catch (error) {
    return error
  }
}

export const getOpenAssessment = async (_mfrProfileId, requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }

  const basePath = `${ApiGateway.assessments}/search`
  const search = { 
      mfrProfileId: _mfrProfileId,
      status: [ASMT_STATUS.NEW, ASMT_STATUS.ACTIVE].join(',')
    }
  const response = await axios.post(basePath, search, requestConfig)
  if (response.data) {
    const { results } = response.data
    return results[0]
  }
  return undefined
}

export const loadOpenAssessment = async (search = {
  manufacturerId: undefined,
  mfrProfileId: undefined
}, requestConfig) => {
  if ( (search.manufacturerId ?? '') === '' && (search.mfrProfileId ?? '') === '') {
    throw new Error('One of manufacturerId, mfrProfileId required to loadOpenAssessment')
  }
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }

  const basePath = `${ApiGateway.assessments}/search`
  const response = await axios.post(basePath, {...search, status: [ASMT_STATUS.NEW, ASMT_STATUS.ACTIVE].join(',') }, requestConfig)
  if (response.data) {
    const { results } = response.data
    return results[0]
  }
  return undefined
}

export const loadAllProductionCodes = async (requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }

  const basePath = `${ApiGateway.facilityManagement}/manufacturer/all-production-codes`
  const response = await axios.get(basePath, requestConfig)
  if (response.data) {
    const payload = response.data

    // Backward/forward compatibility: API may return either
    // { facilities, history } or { results }.
    const facilities = Array.isArray(payload?.facilities) ? payload.facilities : []
    const history = Array.isArray(payload?.history)
      ? payload.history
      : (Array.isArray(payload?.results) ? payload.results : [])

    return {
      ...payload,
      facilities,
      history
    }
  }
  return undefined
}

const hasSpecialCharacters = (value = '') => /[^A-Za-z0-9 ]/.test(value)

const encodeUtf8ToHex = (value = '') => {
  const encoder = new TextEncoder()
  const bytes = encoder.encode(value)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export const saveManufacturer = async (manufacturerUuid, manufacturerDetail, requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }

  const basePath = `${ApiGateway.facilityManagement}/manufacturer${manufacturerUuid ? '/' + manufacturerUuid : ''}`
  const payload = { ...manufacturerDetail }
  const manufacturerAddr = String(payload?.manufacturerAddr ?? '')
  const hasSpecialsInAddress = hasSpecialCharacters(manufacturerAddr)

  const payloadForSubmit = hasSpecialsInAddress
    ? {
      ...payload,
      manufacturerAddr: encodeUtf8ToHex(manufacturerAddr),
      manufacturerAddrTransport: 'hex',
      manufacturerAddrHex: encodeUtf8ToHex(manufacturerAddr)
    }
    : payload

  const response = await axios.put(basePath, payloadForSubmit, requestConfig)
  if (response.data) {
    const { status, instance, created } = response.data
    console.log(`Save: ${status}`,`Instance: ${JSON.stringify(instance,null,2)}`, `Created: ${created}`)
    return response.data
  }
}

export const searchManufacturers = async (search, requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }
    
  const basePath = `${ApiGateway.facilityManagement}/manufacturer/search`
  const response = await axios.post(basePath, search, requestConfig)
  if (response.data) {
    const { results } = response.data
    return results || []
  }
}

export const loadManufacturer = async (_mfrId, requestConfig) => {
  if ( !requestConfig ) {
    requestConfig = await getRequestConfig()
  }
    
  const basePath = `${ApiGateway.facilityManagement}/manufacturer/search`
  const search = {
    searchIncludes: 'ownership'
  }
  if (_mfrId) {
    search.uuid = _mfrId
  }
  const response = await axios.post(basePath, search, requestConfig)
  if (response.data) {
    const { results } = response.data
    return results?.length ? results[0] : {}
  }
}

export const saveMfrProfileChildItem = async (itemType, item, manufacturerProfileId) => {
  const requestConfig = await getRequestConfig()
  const hasItemId = Boolean(item?.id)

  const generateRouteItemId = () => {
    // Prefer platform UUID generation and fallback to RFC4122 v4-compatible generation.
    if (typeof window !== 'undefined' && typeof window?.crypto?.randomUUID === 'function') {
      return window.crypto.randomUUID()
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  // Some environments are deployed with .../{itemType}/{itemId} while others accept .../{itemType} for create.
  // For existing items, always use strict route. For create, try itemType-only first and fallback to strict route.
  const strictPath = `${ApiGateway.manufacturerProfile}/${manufacturerProfileId}/${itemType}/${item?.id || generateRouteItemId()}`
  const createPath = `${ApiGateway.manufacturerProfile}/${manufacturerProfileId}/${itemType}`

  if (hasItemId) {
    const result = await axios.post(strictPath, item, requestConfig)
    return result.data
  }

  try {
    const result = await axios.post(createPath, item, requestConfig)
    return result.data
  } catch (error) {
    // Some deployed APIs require .../{itemType}/{itemId} for create and may return
    // 404/405/500 on the itemType-only route.
    if ([404, 405, 500].includes(error?.response?.status)) {
      const fallback = await axios.post(strictPath, item, requestConfig)
      return fallback.data
    }
    throw error
  }
}

export const createOrUpdateMfrProfileChildItem = async (productItemType, item, itemListSetter, manufacturerProfileId) => {
  try {
    const result = await saveMfrProfileChildItem(productItemType, item, manufacturerProfileId)
    bus.emit(Event.ALERT, { "text": 'Saved', "style": "success", "duration": 3000 })
    itemListSetter(prev => {
      prev = prev && Array.isArray(prev) ? prev : []
      let isNew = true
      const res = prev.reduce((p, c) => {
        if (c.id === result.childItem.id) {
          isNew = false
          p.push(result.childItem)
        } else {
          p.push(c)
        }
        return p
      }, [])
      if (isNew) res.push(result.childItem)

      return res
    })
  } catch (error) {
    console.error(error)
    bus.emit(Event.ALERT, { "text": 'Saving item failed', "style": "error", "duration": 5000 })
    return error
  }
}

export const deleteChildItem = async (childItemType, item, itemListSetter, manufacturerProfileId) => {
  try {
    const itemTypePath = [childItemType, item.id ?? ''].join('/')
    const path = `${ApiGateway.manufacturerProfile}/${manufacturerProfileId}/${itemTypePath}`
    const requestConfig = await getRequestConfig()
    await axios.delete(path, requestConfig)
    bus.emit(Event.ALERT, { "text": 'Deleted', "style": "success", "duration": 3000 })
    itemListSetter(prev => {
      prev = prev && Array.isArray(prev) ? prev : []
      const res = prev.reduce((p, c) => {
        if (c.id !== item.id) {
          p.push(c)
        }
        return p
      }, [])

      return res
    })
  } catch (error) {
    console.error(error)
    bus.emit(Event.ALERT, { "text": 'Failed to delete', "style": "error", "duration": 5000 })
    return error
  }
}
